import { PublicClientApplication } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { copsAuthenticatorConstants as constants } from 'utils/app-configs';

/**
 * Generates a login hint based on the preferred username.
 *
 * This function converts a preferred username from the format [firstName].[lastName]@delaware.pro
 * to [lastName][firstLetterOfFirstName]@delawareconsulting.com. This is necessary to prevent
 * account selection during authentication requests. If the preferred username is not provided,
 * an empty string is returned.
 * Without the correct login hint, ssoSilent/acquireTokenSilent will fail.
 */
// eslint-disable-next-line max-len
// https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-sso#automatically-select-account-on-azure-ad
const getLoginHint = (preferredUsername) => {
  if (!preferredUsername) {
    return '';
  }
  const [names, org] = preferredUsername.split('@');
  const [firstN, lastN] = names.split('.');
  if (org === 'delaware.pro') {
    return `${lastN}${firstN[0]}@delawareconsulting.com`;
  }
  return preferredUsername;
};

/**
 * Decodes the payload of a given JWT token. If the token is invalid or decoding fails,
 * it returns an empty object.
 */
const decodeTokenPayload = (token) => {
  try {
    return jwtDecode(token);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return {};
  }
};

/**
 * Stores the provided access token and token in the local storage.
 */
const storeToken = (accessToken, token) => {
  localStorage.setItem(`auth-${constants.ACCESS_TOKEN}`, accessToken);
  localStorage.setItem(`auth-${constants.TOKEN}`, token);
};

/**
 * Updates the given configuration object with a redirect URI if it is not already present.
 *
 * This function checks if the `redirectUri` property is present in the provided `config` object.
 * If it is not present, it constructs a redirect URI using the current window location
 * and adds it to the `config` object.
 * The constructed redirect URI includes the protocol, hostname, port (if any),
 * and search parameters of the current window location.
 */
export const updateConfigWithRedirectUri = (config) => {
  let updatedConfig = config;
  if (!config.redirectUri) {
    if (!window.location.href) {
      window.location.href =
        // eslint-disable-next-line max-len
        `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;
    }
    updatedConfig = Object.assign({}, updatedConfig, {
      redirectUri: `${window.location.href}${window.location.search}`,
    });
  }
  return updatedConfig;
};

/**
 * Authenticates the application using the provided version and configuration.
 *
 * navigateToLoginRequestUrl: false is needed, without this, acquireTokenRedirect might fail
 */
export const authenticated = async (version, config) => {
  if (version === constants.V2) {
    const cacheLocation = config?.cacheLocation;
    const oAuthConfig = {
      auth: {
        clientId: config?.clientId,
        authority: `https://login.microsoftonline.com/${config?.tenant}/`,
        redirectUri: config?.redirectUri || window.location.href,
        navigateToLoginRequestUrl: false,
        knownAuthorities: config?.knownAuthorities,
        validateAuthority: config?.knownAuthorities,
      },
      cache: {
        cacheLocation,
      },
    };
    const pca = new PublicClientApplication(oAuthConfig);
    pca.addEventCallback((event) => {
      if (event.error) {
        // eslint-disable-next-line no-console
        console.error(`[EVENT] ${event.error.message}`);
      }
    });
    await pca.initialize();
    return pca;
  }
  throw new Error('auth version is not valid');
};

/**
 * Handles the authentication response by storing the tokens and returning one of them.
 */
export const handleAuthResponse = ({ accessToken, idToken }) => {
  storeToken(accessToken, idToken);
  return accessToken || idToken;
};

/**
 * Handle the authentication response from the authenticator.
 *
 * This function performs the following steps:
 * 1. Fetch the authentication configuration from localStorage and parse it as JSON.
 * 2. Handle Error Case
 * 3. If the error message contains AADB2C90118,
 *    initiate a password reset by redirecting to the forgot password authority.
 *    https:// docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
 *    Catch any errors during the redirect and update localError.
 * 4. If there is a response, proceed with handling the successful authentication, store the tokens.
 * 5. Redirect Based on Configuration
 *    If redirectUri is specified and auto-redirect is disabled, redirect to redirectUri.
 *    If auto-redirect is enabled and redirectRoute is valid, redirect to the stored route.
 *    Otherwise, redirect to the origin.
 */
export const authRedirectCallBack = (error, response, pca) => {
  const authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));
  if (error) {
    let localError = error.errorMessage;
    if (error.errorMessage.indexOf('AADB2C90118') > -1) {
      try {
        pca.loginRedirect(authConfig.policies.authorities.forgotPassword);
      } catch (err) {
        localError = err;
      }
    }
    // eslint-disable-next-line no-console
    console.error(localError);
  } else if (response) {
    storeToken(response.accessToken, response.idToken);
    localStorage.setItem(`auth-${constants.ACCOUNT_USERNAME}`, response.account?.username || '');
    localStorage.setItem(`auth-${constants.LOGIN_HINT}`, response.account?.username || '');

    const redirectRoute = localStorage.getItem(`auth-${constants.PATHNAME}`);
    if (authConfig.redirectUri && !authConfig.enableAutoRedirect) {
      window.location = authConfig.redirectUri;
    } else if (authConfig.enableAutoRedirect && redirectRoute && redirectRoute !== '/') {
      window.location.href = `${window.location.origin}/${redirectRoute.substr(
        1,
        redirectRoute.length - 1,
      )}`;
    } else {
      window.location = window.location.origin;
    }
  }
};

/**
 * Checks if the provided token is valid based on its expiration time.
 *
 * The function performs the following steps:
 * 1. If the token is not provided, it returns false.
 * 2. Decodes the token payload to extract the expiration time.
 * 3. Checks if the expiration time is valid and if it is in seconds (less than 13 digits).
 * 4. If the expiration time is in seconds, it compares it with the current time in seconds.
 * 5. If the expiration time is in milliseconds, it compares it with the current time in millisecond
 */
export const isValidToken = (token) => {
  if (!token) {
    return false;
  }
  const decodedToken = decodeTokenPayload(token);
  const expiration = decodedToken && decodedToken.exp;
  if (
    expiration
    && expiration.toString()
    && expiration.toString().length < 13
  ) {
    return expiration && parseInt(expiration, 10) > Date.now() / 1000;
  }
  return expiration && parseInt(expiration, 10) > Date.now();
};

/**
 * Constructs a login request object for the authentication process.
 * If the preferred username is provided, the login request will include a login hint.
 * Otherwise, the login request will only include the scopes.
 */
export const getLoginRequest = (preferredUserName, scopeMSAL) => preferredUserName ?
  {
    scopes: scopeMSAL,
    loginHint: getLoginHint(preferredUserName),
  } :
  {
    scopes: scopeMSAL,
  };

/**
 * Retrieves the authenticator object based on stored authentication version and configuration.
 *
 * This function attempts to get the authentication version and configuration from the local storage
 * If either the authentication version or configuration is not found, the page is reloaded.
 * it is a attempt to redirect to the login page
 * Otherwise, it returns the authenticated object.
 */
export const getAuthenticator = async () => {
  const authVersion = localStorage.getItem(`auth-${constants.AUTH_VERSION}`);
  const authConfig = JSON.parse(localStorage.getItem(`auth-${constants.CONFIG}`));
  // no way of getting the correct config to reauthenticate in background
  if (!authConfig || !authVersion) {
    location.reload();
  }

  const pca = await authenticated(authVersion, authConfig);
  return pca;
};

